import { authRequestError } from '@/utils/authRequestError';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import CryptoJS from 'crypto-js';

import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import { Box } from '@/layout/Box';

import Link from '@/components/UI/Link';

import useAuthValidation from '@/hooks/requireSchema/useAuthValidation';

import { forgotPassword, forgotPasswordVerification, resetPassword } from '@/api/auth.api';
import { AlertModal } from '@/components/UI/Modal';
import { Visible } from '@/components/Icon';
import useResize from '@/hooks/useResize';
import usePasswordResetCooldown from '@/hooks/usePasswordResetCooldown';
import { successHandler } from '@/utils/toastHandler';

interface FormStepperProps {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setUserMail: React.Dispatch<React.SetStateAction<string>>;
  userMail: string;
}

const FormStep1 = ({ setActiveStep, setUserMail, userMail }: FormStepperProps) => {
  // @ts-ignoreL
  const { t } = useTranslation();
  const { isMobile } = useResize();
  const { remaining, refresh } = usePasswordResetCooldown();
  const {
    handleSubmit,
    control,
    setError,
    formState: { isDirty, isSubmitting },
  } = useForm<{ email: string }>();

  const { emailRegister } = useAuthValidation();
  const onSubmit = handleSubmit(async (data) => {
    if (remaining > 0) return;
    try {
      const response = await forgotPassword(data);
      if (!response.data.success) {
        setError('email', { type: 'server', message: t('error.tryAgain') });
        return;
      }
      setUserMail(data.email);
      setActiveStep(2);
      successHandler(t('pages.auth.forgotPassword.message'));
    } catch (error) {
      setError('email', { type: 'server', message: authRequestError(error, t) });
      refresh();
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Text marginTop={'15px'}>{t('pages.auth.forgotPassword.illustrate.step1')}</Text>
      <InputWrapper>
        <Controller
          control={control}
          defaultValue=""
          name={'email'}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              label={t('form.email.label')}
              placeholder={t('form.email.placeholder')}
              variant="standard"
              value={value}
              sx={{ width: '100%' }}
              onChange={onChange}
              error={!!error}
              helperText={error ? error.message : null}
              autoComplete="off"
            />
          )}
          rules={emailRegister}
        />
      </InputWrapper>
      <Box>
        <Button
          width={isMobile ? '100%' : 'unset'}
          size={'lg'}
          variant={'primary'}
          rounded={false}
          marginBottom={15}
          disabled={!isDirty || isSubmitting || remaining > 0}
        >
          {t('buttons.next')}
        </Button>
        {remaining > 0 && <Text role="status">{t('pages.auth.forgotPassword.resendAvailableIn', { seconds: remaining })}</Text>}
        <Text>
          {t('pages.auth.forgotPassword.prompt.step1.context')} <Link to={'/auth/signin'}>{t('buttons.signIn')}</Link>
        </Text>
      </Box>
    </form>
  );
};

interface Step2Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setResetToken: React.Dispatch<React.SetStateAction<string>>;
  userMail: string;
}

const FormStep2 = ({ setActiveStep, userMail, setResetToken }: Step2Props) => {
  // @ts-ignore
  const { t } = useTranslation();
  const { isMobile } = useResize();
  const { remaining, refresh } = usePasswordResetCooldown();
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState('');
  const resendPending = React.useRef(false);
  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { isDirty, isSubmitting },
  } = useForm<{ verificationCode: string }>();

  const { verificationCode } = useAuthValidation();
  const onSubmit = handleSubmit(async (data) => {
    try {
      const res = (await forgotPasswordVerification({ ...data, email: userMail })).data;
      if (res.success) {
        setResetToken(res.data.resetPasswordToken);
        setActiveStep(3);
      }
    } catch (error) {
      setError('verificationCode', {
        type: 'server',
        message: t('error.verification'),
      });
    }
  });

  const resendCode = async () => {
    if (remaining > 0 || resendPending.current || !userMail) return;
    resendPending.current = true;
    setIsResending(true);
    setResendError('');
    try {
      const response = await forgotPassword({ email: userMail });
      if (!response.data.success) {
        setResendError(t('error.tryAgain'));
        return;
      }
      reset({ verificationCode: '' });
      successHandler(t('pages.auth.forgotPassword.message'));
    } catch (error) {
      setResendError(authRequestError(error, t));
    } finally {
      refresh();
      resendPending.current = false;
      setIsResending(false);
    }
  };

  return (
    <form onSubmit={onSubmit}>
      <Text marginTop={'15px'}>{t('pages.auth.forgotPassword.illustrate.step2')}</Text>
      <InputWrapper>
        <Controller
          control={control}
          defaultValue=""
          name={'verificationCode'}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              label={t('form.verificationCode.label')}
              placeholder={t('form.verificationCode.placeholder')}
              variant="standard"
              value={value}
              sx={{ width: '100%' }}
              onChange={onChange}
              error={!!error}
              helperText={error ? error.message : null}
              autoComplete="off"
            />
          )}
          rules={verificationCode}
        />
      </InputWrapper>
      <Box>
        <Button
          width={isMobile ? '100%' : 'unset'}
          size={'lg'}
          variant={'primary'}
          rounded={false}
          marginBottom={15}
          disabled={!isDirty || isSubmitting}
        >
          {t('buttons.verify')}
        </Button>
        <Text>
          {t('pages.auth.forgotPassword.prompt.step2.context')}{' '}
          <Button type="button" variant="regular" disabled={remaining > 0 || isResending} onClick={() => void resendCode()}>
            {t('buttons.resendCode')}
          </Button>
        </Text>
        {remaining > 0 && <Text role="status">{t('pages.auth.forgotPassword.resendAvailableIn', { seconds: remaining })}</Text>}
        {resendError && <Text role="alert">{resendError}</Text>}
      </Box>
    </form>
  );
};

interface Step3Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  userMail: string;
  resetToken: string;
}

const FormStep3 = ({ userMail, resetToken, setActiveStep }: Step3Props) => {
  // @ts-ignore
  const { t } = useTranslation();
  const { isMobile } = useResize();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {
    handleSubmit,
    control,
    setError,
    formState: { isDirty, isSubmitting },
    watch,
  } = useForm<{ password: string; passwordConfirmation: string }>();
  const { passwordRegister } = useAuthValidation();

  const passwordRef = React.useRef('');
  const currentPassword = (passwordRef.current = watch('password', ''));

  const onSubmit = handleSubmit(async (data) => {
    try {
      const res = (
        await resetPassword({
          ...data,
          password: data.password,
          email: userMail,
          resetPasswordToken: resetToken,
        })
      ).data;
      if (res.success) {
        setIsModalOpen(true);
      }
    } catch (error) {
      setError('password', { type: 'server', message: authRequestError(error, t, 'securityAuth.resetFailed') });
    }
  });

  return (
    <>
      <AlertModal
        content={t('passwordResetDone')}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        userMail={userMail}
        type={'resetPW'}
      />
      <form onSubmit={onSubmit}>
        <Text marginTop={'15px'}>{t('pages.auth.forgotPassword.illustrate.step3')}</Text>
        <InputWrapper>
          <Controller
            control={control}
            defaultValue=""
            name={'password'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={t('form.newPassword.label')}
                placeholder={t('form.newPassword.placeholder')}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                InputProps={{ endAdornment: <InputAdornment position="end">
                  <IconButton
                    type="button"
                    edge="end"
                    aria-label={`${t(showPassword ? 'buttons.hidePassword' : 'buttons.showPassword')} ${t('form.newPassword.label')}`}
                    title={t(showPassword ? 'buttons.hidePassword' : 'buttons.showPassword')}
                    onClick={() => setShowPassword((current) => !current)}
                  >
                    {showPassword ? <Visible.VisibleSVG /> : <Visible.VisibleOffSVG />}
                  </IconButton>
                </InputAdornment> }}
              />
            )}
            rules={{
              ...passwordRegister,
              required: t('required.newPassword'),
            }}
          />
          <Controller
            control={control}
            defaultValue=""
            name={'passwordConfirmation'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={t('form.reconfirmNewPassword.label')}
                placeholder={t('form.reconfirmNewPassword.placeholder')}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                type={showConfirmation ? 'text' : 'password'}
                autoComplete="new-password"
                InputProps={{ endAdornment: <InputAdornment position="end">
                  <IconButton
                    type="button"
                    edge="end"
                    aria-label={`${t(showConfirmation ? 'buttons.hidePassword' : 'buttons.showPassword')} ${t('form.reconfirmNewPassword.label')}`}
                    title={t(showConfirmation ? 'buttons.hidePassword' : 'buttons.showPassword')}
                    onClick={() => setShowConfirmation((current) => !current)}
                  >
                    {showConfirmation ? <Visible.VisibleSVG /> : <Visible.VisibleOffSVG />}
                  </IconButton>
                </InputAdornment> }}
              />
            )}
            rules={{
              required: t('required.reconfirmNewPassword'),
              validate: (value) => value === currentPassword || t('error.reconfirmNewPassword'),
            }}
          />
        </InputWrapper>
        <Box>
          <Button
            width={isMobile ? '100%' : 'unset'}
            size={'lg'}
            variant={'primary'}
            rounded={false}
            marginBottom={15}
            disabled={!isDirty || isSubmitting}
          >
            {t('buttons.next')}
          </Button>
        </Box>
        <Button type="button" variant="regular" onClick={() => setActiveStep(1)}>{t('securityAuth.restartReset')}</Button>
      </form>
    </>
  );
};

export { FormStep1, FormStep2, FormStep3 };
